import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { Button, Card, CardBody, CardHeader, Spinner } from "@heroui/react";
import { parseAbsoluteToLocal, parseDateTime } from "@internationalized/date";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchData, usePostData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { PageHeader, ThemedText, ErrorState, Stepper } from "@/components/ui";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuFileText,
    LuWallet,
    LuImage,
} from "react-icons/lu";
import { TransactionInfoStep, DetailPartyStep, MediaStep } from "./components";

const CashTransactionCreate = ({ type }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    const isIncome = type === "in";
    const typeId = isIncome ? 1 : 2;

    const steps = [
        { id: 1, title: "Informasi Transaksi", icon: LuFileText },
        {
            id: 2,
            title: isIncome ? "Detail Pengirim" : "Detail Penerima",
            icon: LuWallet,
        },
        { id: 3, title: "Media & Bukti", icon: LuImage },
    ];

    const [currentStep, setCurrentStep] = useState(1);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: {
            transaction_date: parseAbsoluteToLocal(new Date().toISOString()),
            description: "",
            amount: "",
            transaction_category_id: "",
            master_cash_category_id: "",
            master_cash_id: "",
            party_name: "",
            party_bank_id: "",
            transaction_proof_image: [],
        },
    });

    // ------------------- Data Fetching -------------------

    // 1. Initial Data (Edit Mode)
    const {
        data: initialData,
        isLoading: isLoadingInitial,
        error: errorInitial,
        refetch,
    } = useFetchData(isEditing ? `cash-transaction/${id}/edit` : null);

    // 2. Transaction Categories
    const {
        data: transactionCategories,
        isLoading: isLoadingTransactionCategories,
    } = useFetchData("select/transaction-category", {
        transaction_category_type_id: typeId,
    });

    // 3. Master Cash Categories
    const {
        data: masterCashCategories,
        isLoading: isLoadingMasterCashCategories,
    } = useFetchData("select/master-cash-category");

    // 4. Master Cashes
    const watchMasterCashCategory = watch("master_cash_category_id");
    const { data: masterCashes, isLoading: isLoadingMasterCashes } =
        useFetchData(
            "select/master-cash",
            watchMasterCashCategory
                ? { master_cash_category_id: watchMasterCashCategory }
                : null,
        );

    // 5. Banks
    const { data: banks, isLoading: isLoadingBanks } =
        useFetchData("select/bank");

    // ------------------- Mutations -------------------
    const mutationEndpoint = isEditing
        ? `cash-transaction/${id}/update`
        : "cash-transaction/store";
    const { mutate: mutateData, isLoading: isMutating } =
        usePostData(mutationEndpoint);

    // ------------------- Effects -------------------

    useEffect(() => {
        if (isEditing && initialData) {
            reset({
                transaction_date: parseDateTime(
                    initialData.transaction_date.replace(" ", "T"),
                ),
                description: initialData.description || "",
                amount: initialData.amount,
                transaction_category_id: String(
                    initialData.transaction_category_id,
                ),
                party_name: initialData.party_name || "",
                party_bank_id: initialData.party_bank_id
                    ? String(initialData.party_bank_id)
                    : "",
                master_cash_category_id: initialData.master_cash
                    ? String(initialData.master_cash.master_cash_category_id)
                    : "",
                master_cash_id: String(initialData.master_cash_id),
                transaction_proof_image: initialData.transaction_proof_image
                    ? [
                          {
                              isExisting: true,
                              preview:
                                  initialData.transaction_proof_image.url_600 ||
                                  initialData.transaction_proof_image.url,
                              id: initialData.transaction_proof_image.id,
                          },
                      ]
                    : [],
            });
        }
    }, [isEditing, initialData, reset]);

    // ------------------- Validation & Navigation -------------------

    const validateStep = async (step) => {
        let fields = [];
        switch (step) {
            case 1:
                fields = [
                    "transaction_date",
                    "transaction_category_id",
                    "amount",
                ];
                break;
            case 2:
                fields = ["master_cash_id", "party_name"];
                break;
            default:
                break;
        }
        return await trigger(fields);
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, steps.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi data yang wajib diisi.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    // ------------------- Submit -------------------

    const onSubmit = (data) => {
        const formData = new FormData();
        const date = data.transaction_date.toDate();
        const formattedDate =
            date.getFullYear() +
            "-" +
            String(date.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(date.getDate()).padStart(2, "0") +
            " " +
            String(date.getHours()).padStart(2, "0") +
            ":" +
            String(date.getMinutes()).padStart(2, "0") +
            ":" +
            String(date.getSeconds()).padStart(2, "0");

        formData.append("transaction_date", formattedDate);
        formData.append("description", data.description);
        formData.append("amount", data.amount);
        formData.append(
            "transaction_category_id",
            data.transaction_category_id,
        );
        formData.append("master_cash_id", data.master_cash_id);
        formData.append("party_name", data.party_name);
        if (data.party_bank_id)
            formData.append("party_bank_id", data.party_bank_id);

        if (
            data.transaction_proof_image &&
            data.transaction_proof_image.length > 0
        ) {
            const img = data.transaction_proof_image[0];
            if (!img.isExisting && img.file) {
                formData.append("transaction_proof_photo", img.file);
            }
        }

        if (isEditing) {
            formData.append("_method", "PUT");
        }

        mutateData(formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `${isIncome ? "Pemasukan" : "Pengeluaran"} berhasil ${isEditing ? "diperbarui" : "ditambahkan"}!`,
                );
                navigate("/cash-transaction");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        "Terjadi kesalahan saat menyimpan data",
                );
            },
        });
    };

    if (isEditing && errorInitial) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data transaksi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={
                        isEditing
                            ? isIncome
                                ? "Edit Pemasukan"
                                : "Edit Pengeluaran"
                            : isIncome
                              ? "Catat Pemasukan"
                              : "Catat Pengeluaran"
                    }
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Cash Flow", href: "/cash-transaction" },
                        {
                            label: isEditing
                                ? isIncome
                                    ? "Edit Pemasukan"
                                    : "Edit Pengeluaran"
                                : isIncome
                                  ? "Catat Pemasukan"
                                  : "Catat Pengeluaran",
                        },
                    ]}
                />
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={steps} currentStep={currentStep} />
                </CardBody>
            </Card>

            <form onSubmit={(e) => e.preventDefault()}>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {steps[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {isEditing && isLoadingInitial ? (
                            <div className="flex h-48 items-center justify-center">
                                <Spinner label="Memuat Data..." />
                            </div>
                        ) : (
                            <>
                                {((selectOptions) => (
                                    <>
                                        {/* Step 1: Informasi Transaksi */}
                                        {currentStep === 1 && (
                                            <TransactionInfoStep
                                                control={control}
                                                errors={errors}
                                                selectOptions={selectOptions}
                                                setValue={setValue}
                                            />
                                        )}
                                        {currentStep === 2 && (
                                            <DetailPartyStep
                                                control={control}
                                                errors={errors}
                                                selectOptions={selectOptions}
                                                watchMasterCashCategory={
                                                    watchMasterCashCategory
                                                }
                                                isEditing={isEditing}
                                                setValue={setValue}
                                                isIncome={isIncome}
                                            />
                                        )}
                                    </>
                                ))({
                                    transactionCategories,
                                    isLoadingTransactionCategories,
                                    masterCashCategories,
                                    isLoadingMasterCashCategories,
                                    masterCashes,
                                    isLoadingMasterCashes,
                                    banks,
                                    isLoadingBanks,
                                })}

                                {currentStep === 3 && (
                                    <MediaStep control={control} />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        size="sm"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/cash-transaction")
                                : handlePrev
                        }
                        startContent={<LuArrowLeft />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>

                    {currentStep < steps.length ? (
                        <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onPress={handleNext}
                            endContent={<LuArrowRight />}
                        >
                            Selanjutnya
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            size="sm"
                            isLoading={isMutating}
                            onPress={handleSubmit(onSubmit)}
                            startContent={!isMutating && <LuSave />}
                        >
                            {isEditing ? "Simpan Perubahan" : "Simpan Data"}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default CashTransactionCreate;
